<template>
  <div class="mt-11 h-full w-full rounded-lg bg-white px-6 py-8 shadow-md">
    <div class="flex w-full flex-col items-center justify-start gap-y-4">
      <span class="flex w-full items-center justify-start text-lg font-medium">
        {{ __('Select Attributes') }}
      </span>

      <div class="flex w-full items-center justify-start gap-x-4">
        <button
          class="rounded-lg px-4 py-2"
          v-for="attribute in data.product.category_attributes"
          :class="
            selectedAttributes.find((attr) => attr.id === attribute.id)
              ? 'bg-secondary'
              : 'bg-secondary/60'
          "
          @click="toggleAttribute(attribute)"
        >
          <span class="text-white"> {{ attribute.name }}</span>
        </button>
      </div>
    </div>

    <div
      v-for="(variant, variantIndex) in props.data.product.variants"
      :key="variant.id || variant.temp_id"
      class="relative mt-8 grid grid-cols-2 gap-4 rounded-lg border p-6 shadow-md"
    >
      <Icon
        v-if="props.data.product.variants.length > 1"
        type="filled"
        class="absolute right-2 top-2 cursor-pointer text-xl text-primary hover:text-red-500"
        name="delete"
        @click="handleDeleteVariant(variantIndex)"
      />

      <div
        v-for="(attribute, attributeIndex) in variant.attributes"
        :key="attribute.id"
        class="col-span-2 sm:col-span-1"
      >
        <label class="mb-2 block text-sm text-[#58585E]"
          >{{ attribute.name }}
        </label>
        <FormSingleselect
          v-model="attribute.value"
          :values="getAttributeValues(attribute, variantIndex)"
          :placeholder="__('Add ') + attribute.name"
          class="w-full"
          :errors="
            errors[
              `variants.${variantIndex}.attributes.${attributeIndex}.value.id`
            ]
          "
        />
      </div>

      <Input
        v-model="variant.stock"
        :value="variant.stock"
        class="col-span-2"
        :placeholder="__('Add stock')"
        :label="__('Available stock')"
        type="number"
        onkeydown=""
        :errors="errors[`variants.${variantIndex}.stock`]"
      />
    </div>

    <div
      v-if="data.product.variants?.length > 0"
      class="mt-4 flex w-full items-center justify-start"
    >
      <button
        class="btn btn-ghost flex items-center justify-center gap-x-4"
        :disabled="!canAddNewVariant"
        :class="{ 'cursor-not-allowed opacity-50': !canAddNewVariant }"
        @click="handleAddVariant"
      >
        <Icon name="add_box" type="filled" class="text-xl"></Icon>
        <span class="text-primary">{{ __('Add More Variants') }} </span>
      </button>
    </div>

    <div
      v-if="data.product.variants?.length > 0"
      class="mt-4 flex w-full items-center justify-center gap-x-4"
    >
      <button
        class="btn btn-ghost flex items-center justify-center gap-x-4"
        @click="$emit('cancel')"
      >
        <span class="text-primary">{{ __('Cancel') }} </span>
      </button>
      <button
        class="btn btn-fill flex items-center justify-center gap-x-4"
        @click="$emit('submitForm')"
        :disabled="isLoading"
      >
        <span
          >{{
            headingText === __('Edit Product')
              ? __('Update Product')
              : __('Add Product')
          }}
        </span>
        <Icon
          v-if="isLoading"
          class="animate-spin text-lg"
          name="progress_activity"
        ></Icon>
      </button>
    </div>
  </div>
</template>
<script setup>
  import { ref, computed } from 'vue';

  import Icon from '@/components/global/Icon.vue';
  import { __ } from '@/helpers';
  import Input from '@/components/global/Form/Input.vue';
  import FormSingleselect from '@/components/global/Form/FormSingleselect.vue';

  const props = defineProps({
    data: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    selectedAttributes: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean
    },
    headingText: {
      type: String
    }
  });
  const variantsKey = ref(0);
  const toggleAttribute = (attribute) => {
    if (props.selectedAttributes.length == 0) {
      props.data.product.variants = [
        {
          attributes: [],
          stock: null
        }
      ];
    }
    const index = props.selectedAttributes.findIndex(
      (attr) => attr.id === attribute.id
    );
    if (index !== -1) {
      props.data.product.variants.map((variant) => {
        if (variant.deleted_attributes_ids === undefined) {
          variant.deleted_attributes_ids = [];
        }
        variant.deleted_attributes_ids.push(attribute.id);
      });
      props.selectedAttributes.splice(index, 1);
      props.data.product.variants.forEach((variant) => {
        variant.attributes.splice(
          variant.attributes.findIndex((attr) => attr.id === attribute.id),
          1
        );
      });
    } else {
      props.selectedAttributes.push(attribute);

      // Add this attribute to all existing variants
      props.data.product.variants.forEach((variant) => {
        variant.attributes.push({
          name: attribute.name,
          value: null,
          id: attribute.id
        });
      });
    }

    if (props.selectedAttributes.length == 0) {
      props.data.product.variants = [];
    }
  };

  const handleAddVariant = () => {
    const newVariant = {
      attributes: props.data.product.variants[0].attributes.map((attr) => ({
        ...attr,
        value: null
      })),
      stock: null,
      temp_id: Date.now() + Math.random()
    };

    props.data.product.variants.push(newVariant);
  };

  const handleDeleteVariant = (variantIndex) => {
    const deletedVariant = props.data.product.variants[variantIndex];
    if (props.data.product.deleted_variants_ids === undefined) {
      props.data.product.deleted_variants_ids = [];
    }
    if (deletedVariant.id) {
      props.data.product.deleted_variants_ids.push(deletedVariant.id);
    }
    props.data.product.variants.splice(variantIndex, 1);
  };

  const getCompleteCombinations = () => {
    const completedCombinations = props.data.product.variants
      .filter((variant) =>
        variant.attributes.every((attr) => attr.value != null)
      )
      .map((variant) =>
        variant.attributes
          .map((attr) => `${attr.name}:${attr.value.name}`)
          .sort()
          .join('|')
      );

    return completedCombinations ?? [];
  };

  const getAttributeValues = (attribute, variantIndex = null) => {
    const currentVariant = props.data.product.variants[variantIndex];

    if (currentVariant === undefined || currentVariant === null) {
      return [];
    }
    const foundAttribute = props.data.product.category_attributes.find(
      (attr) => attr.id == attribute.id
    );

    const selectedValues = currentVariant.attributes
      .filter((attr) => attr.id != attribute.id && attr.value !== null)
      .map((attr) => ({ name: attr.name, value: attr.value.name }));

    const maxPossibleOccurences =
      getNumberOfCombinationsPossible() / foundAttribute.values.length;

    const completedCombinations = getCompleteCombinations();

    let availableValues = getAvailableValues(
      foundAttribute,
      completedCombinations,
      selectedValues,
      maxPossibleOccurences,
      attribute
    );

    filterImpossibleVariantsAfterAttributeChange(
      attribute,
      maxPossibleOccurences
    );
    // also eliminate if the variant already exists (the same combination)
    filterExistingVariants(currentVariant);

    return availableValues;
  };

  const canAddNewVariant = computed(() => {
    const lastVariant =
      props.data.product.variants[props.data.product.variants.length - 1];
    const isLastVariantComplete = lastVariant.attributes.every(
      (attribute) => attribute.value != null
    );

    return (
      isLastVariantComplete &&
      props.data.product.variants.length !== getNumberOfCombinationsPossible()
    );
  });

  const getNumberOfCombinationsPossible = () => {
    return props.selectedAttributes.reduce((accumulator, currentAttribute) => {
      return currentAttribute.values.length * accumulator;
    }, 1);
  };

  const filterExistingVariants = (currentVariant) => {
    if (currentVariant && currentVariant.attributes) {
      const existingCombination = currentVariant.attributes
        .map((attr) => `${attr.name}:${attr.value?.name}`)
        .sort()
        .join('|');

      if (existingCombination && existingCombination !== '') {
        const existingCombinationsCount = props.data.product.variants.filter(
          (variant) =>
            variant.attributes
              .map((attr) => `${attr.name}:${attr.value?.name}`)
              .sort()
              .join('|') === existingCombination
        ).length;

        // remove if the combination is found more then 1 time, but remove the last occurence
        if (existingCombinationsCount > 1) {
          const lastCombinationInArrayIndex =
            props.data.product.variants.findLastIndex(
              (variant) =>
                variant.attributes
                  .map((attr) => `${attr.name}:${attr.value?.name}`)
                  .sort()
                  .join('|') === existingCombination
            );
          if (props.data.product.deleted_variants_ids === undefined) {
            props.data.product.deleted_variants_ids = [];
          }
          if (
            props.data.product.variants[lastCombinationInArrayIndex].id !==
            undefined
          ) {
            props.data.product.deleted_variants_ids.push(
              props.data.product.variants[lastCombinationInArrayIndex].id
            );
          }
          props.data.product.variants = props.data.product.variants.filter(
            (variant, i) => i !== lastCombinationInArrayIndex
          );
          variantsKey.value++;
        }
      }
    }
  };

  const filterImpossibleVariantsAfterAttributeChange = (
    attribute,
    maxPossibleOccurences
  ) => {
    let currentAttribute = `${attribute.name}:${attribute.value?.name}`;
    let completeCombinations = getCompleteCombinations();
    let occurencesUntilNow = completeCombinations.filter((comb) =>
      comb.includes(currentAttribute)
    ).length;

    if (occurencesUntilNow > maxPossibleOccurences) {
      const index = props.data.product.variants.findLastIndex((variant) =>
        variant.attributes.some(
          (attr) =>
            attr.id === attribute.id &&
            attr.value?.name === attribute.value.name
        )
      );
      if (index > -1) {
        if (props.data.product.deleted_variants_ids === undefined) {
          props.data.product.deleted_variants_ids = [];
        }
        if (props.data.product.variants[index].id) {
          props.data.product.deleted_variants_ids.push(
            props.data.product.variants[index].id
          );
        }

        props.data.product.variants = props.data.product.variants.filter(
          (variant, i) => i !== index
        );
        variantsKey.value++;
      }
    }
  };

  const getAvailableValues = (
    foundAttribute,
    completedCombinations,
    selectedValues,
    maxPossibleOccurences,
    attribute
  ) => {
    return foundAttribute.values.filter((currentValue) => {
      const currentValueAppearences = completedCombinations.filter(
        (combination) =>
          combination.includes(`${foundAttribute.name}:${currentValue.name}`)
      ).length;

      const hasMaxAppearences =
        currentValueAppearences >= maxPossibleOccurences;

      if (hasMaxAppearences) {
        return false;
      }

      const testCombination = [
        ...selectedValues,
        { name: attribute.name, value: currentValue.name }
      ]
        .map((attr) => `${attr.name}:${attr.value}`)
        .sort()
        .join('|');

      if (completedCombinations.includes(testCombination)) {
        return false;
      }

      return true;
    });
  };
</script>
